import React, { useMemo } from "react";
import { Dialog, DialogContent, TextField, Grid, DialogActions, Button, Typography, DialogTitle, Divider, } from "@material-ui/core";
import { SelectListField } from "../../../common/SelectListField";
import { useFormState } from "../../../../hooks/useFormState";
import { useDispatch, useSelector } from "react-redux";
import { updateComplaint, submitCloseCase } from "../../../../redux/actions";
import { selectComplaint } from "../../../../redux/selectors";
import { natureOfAllegationService, natureOfComplaintService, subNatureOfAllegationService, subNatureOfComplaintService, complaintOutcomeService, regulatoryActionTakenService, riskTypesService, sourceOfAllegationService, finalDeterminationService, actionsArisingService, disclosureActionsTakenService, generalConditionsService, aoActionsService, qualificationConditionsService, subjectConditionsService, internalReviewOutcomesService, internalReviewReasonsService, internalReviewActionsService, } from "../../../../services";
import { assesIsWbMalpracticeMode } from "../../../../utils/caseTypeHelper";
import { toClientModel, fromClientModel } from "../../../../utils/caseDetailsAdapter";
import { useFieldLookup } from "../../../../hooks/useFieldLookup";
import { KeyboardDatePicker } from "@material-ui/pickers";
import { isFutureDate } from "../../../../utils/dateUtils";
import { format } from "date-fns";
const MULTILINE_ROW_HEIGHT = 8;
const DATE_TIME_FORMAT = "dd/MM/yyyy";
const DEFAULT_MODEL = {
    generalConditions: [],
    qualificationConditions: [],
    subjectConditions: [],
    aoActions: [],
    internalOutcomeReason: [],
    closedDate: new Date(),
};
const formatDate = (date) => format(date, "yyyy-MM-dd") + "T10:00:00.000";
const needToValidate = (outcomeVal) => !(outcomeVal === "notWithinRemit" || outcomeVal === "duplicate" || outcomeVal === "error");
export const CloseCaseDialog = ({ isDialogOpen, onDialogClose, caseId, }) => {
    const dispatch = useDispatch();
    const complaintState = toClientModel(useSelector(selectComplaint).data);
    const isMalpractice = assesIsWbMalpracticeMode(complaintState.caseType);
    const INTERNAL_REVIEW_STATE_VALUES = [
        "reviewPending",
        "reviewAccepted",
        "reviewRejected",
        "reviewCompleted",
    ];
    const isInternalReview = (caseType) => INTERNAL_REVIEW_STATE_VALUES.some(typeId => typeId === caseType);
    const isMalpracticeOrOfqual = assesIsWbMalpracticeMode(complaintState.caseType) ||
        complaintState.caseType === "complaintAboutOfqual" ||
        complaintState.caseType === "executivePolicy" ||
        complaintState.caseType === "executiveComplaint";
    const validationRules = useMemo(() => {
        const validateIfNotPrematureComplaint = ({ state, requiredFn, val }) => needToValidate(state.values["outcome"]) ? requiredFn(val) : "";
        const malpracticeRules = [
            ["sanction", validateIfNotPrematureComplaint],
            ["action", validateIfNotPrematureComplaint],
            ["aoActions", validateIfNotPrematureComplaint],
        ];
        const internalOutcomeRules = [
            ["reviewOutcome", validateIfNotPrematureComplaint],
            ["internalOutcomeReason", validateIfNotPrematureComplaint],
            ["internalOutcomeAction", validateIfNotPrematureComplaint],
        ];
        const standardRules = ["outcome"];
        const coreRules = [
            ["summary", validateIfNotPrematureComplaint],
            ["nature", validateIfNotPrematureComplaint],
            ["subNature", validateIfNotPrematureComplaint],
            ["regulatoryActionTaken", validateIfNotPrematureComplaint],
            ["riskType", validateIfNotPrematureComplaint],
            ["allegationSource", validateIfNotPrematureComplaint],
            ["finalDetermination", validateIfNotPrematureComplaint],
            [
                "closedDate",
                ({ state, val }) => {
                    if (!val) {
                        return "Required";
                    }
                    if (isFutureDate(val)) {
                        return "Date must be in the past";
                    }
                },
            ],
        ];
        if (!isMalpracticeOrOfqual) {
            coreRules.push([
                "generalConditions",
                () => {
                    return "";
                },
            ], [
                "qualificationConditions",
                () => {
                    return "";
                },
            ], [
                "subjectConditions",
                () => {
                    return "";
                },
            ], [
                "specialConditions",
                () => {
                    return "";
                },
            ]);
        }
        let ruleArray = [...coreRules];
        if (isMalpractice) {
            ruleArray = [...ruleArray, ...malpracticeRules];
        }
        else {
            ruleArray = [...ruleArray, ...standardRules];
        }
        if (isInternalReview(complaintState.stage)) {
            ruleArray = [...ruleArray, ...internalOutcomeRules];
        }
        return ruleArray;
    }, [isMalpractice]);
    const fieldsToDisplay = validationRules
        .map(rule => (typeof rule === "string" ? rule : rule[0]))
        .filter(fieldName => {
        const val = complaintState[fieldName];
        return !val || (Array.isArray(val) && !val.length);
    });
    const fieldsToDisplayValidationRules = validationRules.filter(rule => {
        const ruleKey = typeof rule === "string" ? rule : rule[0];
        return fieldsToDisplay.includes(ruleKey);
    });
    const model = useMemo(() => (Object.assign(Object.assign({}, DEFAULT_MODEL), { 
        // we make sure summary is in the model that the form gets so that validation rules are guaranteed to have
        //  it to refer to (either outcome is known beforehand so we have the value and the field is hidden, or we have no outcome
        //  beforehand and so the validation will use the live value for the outcome field)
        outcome: complaintState["outcome"] })), [complaintState]);
    const { state, formProps, triggerSubmit, triggerReset, handleOnChange } = useFormState(model, fieldsToDisplayValidationRules, ({ model, patch }) => {
        const convertedPatch = fromClientModel(model, patch).filter(op => !op.path.endsWith("noteBody") && !op.path.endsWith("closedDate"));
        dispatch(submitCloseCase(caseId, {
            noteBody: model.noteBody,
            closedDate: formatDate(model.closedDate),
        }));
        if (convertedPatch.length) {
            dispatch(updateComplaint(caseId, convertedPatch));
        }
        onDialogClose();
        triggerReset();
    });
    const outcome = useFieldLookup(complaintOutcomeService, state.values["outcome"]);
    return (React.createElement(Dialog, { open: isDialogOpen, onClose: () => {
            onDialogClose();
            triggerReset();
        }, maxWidth: "md", fullWidth: true },
        React.createElement(DialogTitle, null, "Close case"),
        React.createElement(Divider, null),
        React.createElement(DialogContent, null,
            React.createElement("form", null,
                React.createElement(Grid, { container: true, spacing: 3 },
                    fieldsToDisplay.includes("outcome") && (React.createElement(Grid, { item: true, xs: 12, sm: 12 },
                        React.createElement(SelectListField, Object.assign({}, formProps("outcome"), { label: "Outcome", service: complaintOutcomeService, dataTest: "close-outcome-list" })))),
                    !needToValidate(state.values["outcome"]) && (React.createElement(Grid, { item: true, xs: 12, sm: 12 },
                        React.createElement(Typography, null,
                            "The outcome ",
                            React.createElement("strong", null, outcome),
                            " means that the remaining fields are optional"),
                        React.createElement(Typography, null,
                            "If this outcome is incorrect, it can be changed on the",
                            " ",
                            React.createElement("strong", null, "Case Details"),
                            " tab"))),
                    fieldsToDisplay.includes("allegationSource") && (React.createElement(Grid, { item: true, xs: 12, sm: 12 },
                        React.createElement(SelectListField, Object.assign({}, formProps("allegationSource"), { label: isMalpractice ? "Source of Allegation" : "Source of Complaint", service: sourceOfAllegationService, dataTest: "close-source-list" })))),
                    fieldsToDisplay.includes("nature") && (React.createElement(Grid, { item: true, xs: 12, sm: 12 },
                        React.createElement(SelectListField, Object.assign({}, formProps("nature"), { onChange: e => {
                                var _a;
                                handleOnChange({
                                    name: "nature",
                                    value: (_a = e === null || e === void 0 ? void 0 : e.target) === null || _a === void 0 ? void 0 : _a.value,
                                });
                                if (state.values.subNature) {
                                    handleOnChange({ name: "subNature", value: "" });
                                }
                            }, label: isMalpractice ? "Nature of Allegation" : "Nature of Complaint", service: isMalpractice ? natureOfAllegationService : natureOfComplaintService, dataTest: "close-nature-list" })))),
                    fieldsToDisplay.includes("subNature") && (React.createElement(Grid, { item: true, xs: 12, sm: 12 },
                        React.createElement(SelectListField, Object.assign({}, formProps("subNature"), { label: isMalpractice ? "Sub Nature of Allegation" : "Sub Nature of Complaint", service: {
                                getAll: _ => isMalpractice
                                    ? subNatureOfAllegationService.getAll()
                                    : subNatureOfComplaintService.getAll({
                                        query: state.values.nature,
                                    }),
                            }, disabled: isMalpractice ? false : !state.values.nature, dataTest: "close-subnature-list" })))),
                    fieldsToDisplay.includes("summary") && (React.createElement(Grid, { item: true, xs: 12, sm: 12 },
                        React.createElement(TextField, Object.assign({ variant: "outlined" }, formProps("summary"), { label: "Summary", "data-test": "close-summary-field", fullWidth: true, multiline: true, rows: MULTILINE_ROW_HEIGHT })))),
                    fieldsToDisplay.includes("action") && (React.createElement(Grid, { item: true, xs: 12, sm: 12 },
                        React.createElement(SelectListField, Object.assign({}, formProps("action"), { 
                            // auto select none if referredTOAO or noActionRequired selected
                            onChange: target => {
                                var _a, _b, _c;
                                handleOnChange({
                                    name: "action",
                                    value: (_a = target === null || target === void 0 ? void 0 : target.target) === null || _a === void 0 ? void 0 : _a.value,
                                });
                                if (((_b = target === null || target === void 0 ? void 0 : target.target) === null || _b === void 0 ? void 0 : _b.value) === "referredToAO" ||
                                    ((_c = target === null || target === void 0 ? void 0 : target.target) === null || _c === void 0 ? void 0 : _c.value) === "noActionRequired") {
                                    if (!state.values.aoActions || state.values.aoActions.length === 0) {
                                        handleOnChange({ name: "aoActions", value: ["none"] });
                                    }
                                    if (!state.values.sanction) {
                                        handleOnChange({ name: "sanction", value: "none" });
                                    }
                                    if (!state.values.finalDetermination) {
                                        handleOnChange({ name: "finalDetermination", value: "NA" });
                                    }
                                }
                            }, label: "Action taken", service: disclosureActionsTakenService })))),
                    fieldsToDisplay.includes("aoActions") && (React.createElement(Grid, { item: true, xs: 12, sm: 12 },
                        React.createElement(SelectListField, Object.assign({}, formProps("aoActions"), { label: "AO Action Taken", multiple: true, service: aoActionsService })))),
                    fieldsToDisplay.includes("sanction") && (React.createElement(Grid, { item: true, xs: 12, sm: 12 },
                        React.createElement(SelectListField, Object.assign({}, formProps("sanction"), { label: "AO Sanctions/ Actions arising", service: actionsArisingService })))),
                    fieldsToDisplay.includes("finalDetermination") && (React.createElement(Grid, { item: true, xs: 12, sm: 12 },
                        React.createElement(SelectListField, Object.assign({}, formProps("finalDetermination"), { label: "Final Determination", service: finalDeterminationService, dataTest: "close-final-determination-list" })))),
                    fieldsToDisplay.includes("regulatoryActionTaken") && (React.createElement(Grid, { item: true, xs: 12, sm: 12 },
                        React.createElement(SelectListField, Object.assign({}, formProps("regulatoryActionTaken"), { label: "Regulatory Action Taken", service: regulatoryActionTakenService, dataTest: "close-regulatory-action-list" })))),
                    fieldsToDisplay.includes("generalConditions") && (React.createElement(Grid, { item: true, xs: 12, sm: 12 },
                        React.createElement(SelectListField, Object.assign({}, formProps("generalConditions"), { label: "General Conditions", multiple: true, service: generalConditionsService, dataTest: "close-general-conditions-list" })))),
                    fieldsToDisplay.includes("qualificationConditions") && (React.createElement(Grid, { item: true, xs: 12, sm: 12 },
                        React.createElement(SelectListField, Object.assign({}, formProps("qualificationConditions"), { label: "Qualification Level Conditions", multiple: true, service: qualificationConditionsService, dataTest: "close-qual-level-conditions-list" })))),
                    fieldsToDisplay.includes("subjectConditions") && (React.createElement(Grid, { item: true, xs: 12, sm: 12 },
                        React.createElement(SelectListField, Object.assign({}, formProps("subjectConditions"), { label: "Subject Level Conditions", multiple: true, service: subjectConditionsService, dataTest: "close-subj-level-conditions-list" })))),
                    fieldsToDisplay.includes("specialConditions") && (React.createElement(Grid, { item: true, xs: 12, sm: 12 },
                        React.createElement(TextField, Object.assign({ variant: "outlined" }, formProps("specialConditions"), { label: "Special Conditions", fullWidth: true, inputProps: { maxLength: 2000 }, "data-test": "close-special-conditions-field" })))),
                    fieldsToDisplay.includes("riskType") && (React.createElement(Grid, { item: true, xs: 12, sm: 12 },
                        React.createElement(SelectListField, Object.assign({}, formProps("riskType"), { label: "Risk Type", service: riskTypesService, dataTest: "risk-type-list" })))),
                    fieldsToDisplay.includes("reviewOutcome") && (React.createElement(Grid, { item: true, xs: 12, sm: 12 },
                        React.createElement(SelectListField, Object.assign({}, formProps("reviewOutcome"), { label: "Internal review - Outcome", service: internalReviewOutcomesService, "data-test": "internal-rev-outcome-list" })))),
                    fieldsToDisplay.includes("internalOutcomeReason") && (React.createElement(Grid, { item: true, xs: 12, sm: 12 },
                        React.createElement(SelectListField, Object.assign({}, formProps("internalOutcomeReason"), { label: "Internal review - Criteria because the following were not adhered to", multiple: true, service: internalReviewReasonsService, "data-test": "internal-rev-criteria-list" })))),
                    fieldsToDisplay.includes("internalOutcomeAction") && (React.createElement(Grid, { item: true, xs: 12, sm: 12 },
                        React.createElement(SelectListField, Object.assign({}, formProps("internalOutcomeAction"), { label: "Internal review - Actions arising", service: internalReviewActionsService, "data-test": "internal-rev-actions-list" })))),
                    React.createElement(Grid, { item: true, xs: 12, sm: 12 },
                        React.createElement(KeyboardDatePicker, Object.assign({}, formProps("closedDate"), { label: "Closed Date", "data-test": "close-date-picker", inputVariant: "outlined", format: DATE_TIME_FORMAT, KeyboardButtonProps: {
                                "aria-label": "change date",
                            }, autoOk: true, fullWidth: true }))),
                    React.createElement(Grid, { item: true, xs: 12, sm: 12 },
                        React.createElement(TextField, Object.assign({ variant: "outlined" }, formProps("noteBody"), { label: "Close Case Note", "data-test": "close-case-note-field", fullWidth: true, multiline: true, rows: MULTILINE_ROW_HEIGHT })))))),
        React.createElement(DialogActions, null,
            React.createElement(Button, { onClick: () => triggerSubmit(), "data-test": "close-case-modal-button", color: "primary" }, "Close case"))));
};
